// #ux-redesign-start: LaboratoryDetail rediseñado (Paso 5 — Patients)
// Cambios clave:
//   - Resultados agrupados por clasificador via <StudyAnalytes/> (servicio
//     compartido `AnalytesGroupsService`). Acordeón + buscador + copy-row.
//   - Metadata en SectionCard compacto, 2 columnas en md+, 1 en mobile.
//   - Tabs custom limpios (PDF / Detail / Review) con paneles bajo demanda.
//   - PDF panel mejorado vía ReportViewer rediseñado.
//   - Acciones del header: Back + PDF + Fragen? (modal DS).
//   - i18n completo en 4 idiomas.
// ─────────────────────────────────────────────────────────────────────────────

import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useTranslate } from "react-translate";

import LaboratoryService from "../../services/LaboratoryService";
import { actionReadNotification } from "store/actions/notificationsActions";
import ReportViewer from "components/Laboratories/ReportViewer/ReportViewerComponent";
import PdfLoadingButton from "components/Common/Laboratory/LaboratoryPdfDownload";
import StudyAnalytes from "components/Laboratories/StudyAnalytes";
import useResponsive from "hooks/useResponsive";

import {
  Button,
  Badge,
  PageContainer,
  SectionCard,
  Modal,
  LoadingBlock,
  EmptyState,
} from "components/ds";

const LaboratoryDetail = () => {
  const dispatch = useDispatch();
  const { reportId } = useParams("reportId");
  const [study, setStudy] = useState("loading");
  const user = useSelector((state) => state.userReducer);
  const history = useHistory();
  const t = useTranslate("laboratory-detail");
  const thl7 = useTranslate("hl7-status");

  const notifications = useSelector(
    (state) => state.notificationsReducer["laboratory"]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const toggleModal = () => setModalOpen((v) => !v);

  const { isMobile } = useResponsive();
  // Meta colapsada por defecto en mobile, expandida en desktop. El usuario
  // puede togglear en ambos casos.
  const [metaExpanded, setMetaExpanded] = useState(!isMobile);

  const [activeTab, setActiveTab] = useState(
    window.location.href.includes("review") ? "review" : "pdf"
  );

  useEffect(() => {
    notifications.notifications.forEach((n) => {
      if (activeTab === "review") {
        if (n.notification.text.toLowerCase().match(/review|kommentar/)) {
          dispatch(actionReadNotification(n.notification._id, true));
        }
      } else if (n.notification.text.toLowerCase().match(/studie|laborbefund/)) {
        dispatch(actionReadNotification(n.notification._id, true));
      }
    });
  }, [notifications, activeTab, dispatch]);

  const fetchData = async (_reportId, patientId) => {
    try {
      const { data, status } = await LaboratoryService.getStudyDetail(
        _reportId,
        patientId
      );
      if (status >= 400 || !data) {
        history.replace("/admin/lab-studies");
        return;
      }
      const { doctor_id, patient_id, hl7, doctor, reviews } = data;
      const medicalValues = await JSON.parse(hl7[0].reportData);
      const metadata = hl7[0];
      const reportFile = hl7[0].reportFile ? hl7[0].reportFile : null;
      setStudy({
        doctor_id,
        patient_id,
        medicalValues,
        doctor,
        metadata,
        reportFile,
        reviews: [...reviews],
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const patient_ext_ref =
      user.practices && user.practices.length
        ? user.practices[0].patient_ext_ref
        : null;
    fetchData(reportId, patient_ext_ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    if (study.metadata && !study.reportFile && activeTab === "pdf") {
      setActiveTab("detail");
    }
  }, [study]); // eslint-disable-line react-hooks/exhaustive-deps

  if (study === "loading") {
    return (
      <PageContainer
        title={t("laboratory-details")}
        icon="microscope"
        subtitle={t("loading-study")}
      >
        <LoadingBlock />
      </PageContainer>
    );
  }

  if (!study || !study.metadata) {
    return (
      <PageContainer title={t("laboratory-details")} icon="microscope">
        <EmptyState
          title={t("study-not-found")}
          actions={
            <Button
              variant="primary"
              onClick={() => history.push("/admin/lab-studies")}
            >
              {t("go-back")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  // tabs DS
  const tabs = [];
  if (study.reportFile && study.reportFile.length) {
    tabs.push({ key: "pdf", label: t("tab-pdf") });
  }
  if (study.medicalValues && study.medicalValues.length) {
    tabs.push({ key: "detail", label: t("detail") });
  }
  tabs.push({ key: "review", label: t("doctor-review") });

  // header actions (back · PDF · Fragen)
  const actions = (
    <div className="ds-lab-detail__actions">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => history.push("/admin/lab-studies")}
        iconLeft={
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        }
      >
        {t("go-back")}
      </Button>
      {study.reportFile && (
        <PdfLoadingButton
          reportId={reportId}
          patientId={study.patient_id}
          doctorId={study.doctor_id}
          prompt={t("pdf-download")}
          buttonSize="sm"
        />
      )}
      <Button variant="primary" size="sm" onClick={toggleModal}>
        {t("consultation") || "Fragen?"}
      </Button>
    </div>
  );

  return (
    <>
      <PageContainer
        title={t("laboratory-details")}
        icon="microscope"
        actions={actions}
      >
        {/* Metadata — summary 1 línea + detalle colapsable. En mobile arranca
            cerrado para que el PDF aparezca arriba; el usuario expande con un
            botón único. En desktop arranca abierto (espacio sobra). */}
        {study.metadata && study.doctor && (
          <SectionCard padding={isMobile ? "sm" : "md"}>
            <div className="ds-lab-detail__meta-bar">
              <div className="ds-lab-detail__meta-summary">
                <span className="ds-lab-detail__meta-doctor">
                  {study.doctor.title} {study.doctor.firstName}{" "}
                  {study.doctor.lastName}
                </span>
                <span
                  className="ds-lab-detail__meta-sep"
                  aria-hidden="true"
                >
                  ·
                </span>
                <span className="ds-lab-detail__meta-date">
                  {moment(study.metadata.created_at, "YYYYMMDDhhmmss").format(
                    user.dateFormat
                  )}
                </span>
                {study.metadata.reportStatus && (
                  <Badge variant="neutral" size="sm">
                    {thl7(study.metadata.reportStatus)}
                  </Badge>
                )}
              </div>
              <button
                type="button"
                className="ds-lab-detail__meta-toggle"
                onClick={() => setMetaExpanded((v) => !v)}
                aria-expanded={metaExpanded}
                aria-controls="ds-lab-detail-meta"
                aria-label={
                  metaExpanded
                    ? t("collapse-details") || t("collapse-all")
                    : t("expand-details") || t("expand-all")
                }
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transform: metaExpanded ? "rotate(180deg)" : "none",
                    transition: "transform 120ms ease",
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>

            {metaExpanded && (
              <dl className="ds-lab-detail__meta" id="ds-lab-detail-meta">
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("doctor")}</dt>
                  <dd>
                    {study.doctor.title} {study.doctor.firstName}{" "}
                    {study.doctor.lastName}
                  </dd>
                </div>
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("order")}</dt>
                  <dd>{study.metadata.adminValues.order_number}</dd>
                </div>
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("control")}</dt>
                  <dd>{study.metadata.adminValues.control_number}</dd>
                </div>
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("report-date")}</dt>
                  <dd>
                    {moment(study.metadata.updated_at, "YYYYMMDDhhmmss").format(
                      user.dateFormat
                    )}
                  </dd>
                </div>
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("reception-date")}</dt>
                  <dd>
                    {moment(study.metadata.created_at, "YYYYMMDDhhmmss").format(
                      user.dateFormat
                    )}
                  </dd>
                </div>
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("status")}</dt>
                  <dd>{thl7(study.metadata.reportStatus)}</dd>
                </div>
                <div className="ds-lab-detail__meta-item">
                  <dt>{t("study-type")}</dt>
                  <dd>
                    {study.metadata.adminValues.study_type
                      ? t(
                          study.metadata.adminValues.study_type
                            .toLowerCase()
                            .replace(" ", "-")
                        )
                      : t("unknown")}
                  </dd>
                </div>
              </dl>
            )}
          </SectionCard>
        )}

        {/* Tabs custom (mount/unmount per panel) */}
        <nav
          className="ds-lab-detail__tabs"
          role="tablist"
          aria-label={t("tabs-aria")}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`ds-lab-detail__tab ${
                activeTab === tab.key ? "is-active" : ""
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="ds-lab-detail__panel" role="tabpanel">
          {activeTab === "detail" && <StudyAnalytes study={study} />}

          {activeTab === "review" && (
            <div className="ds-lab-detail__review">
              {study.reviews.length ? (
                study.reviews.map((r, i) => (
                  <SectionCard key={`review-${i}`}>
                    <div className="ds-lab-detail__review-header">
                      <small>
                        {moment(
                          r.updated_at ? r.updated_at : r.created_at
                        ).format(`${user.dateFormat} HH:mm`)}
                      </small>
                    </div>
                    <div
                      className="ds-lab-detail__review-content"
                      dangerouslySetInnerHTML={{
                        __html: r && r.content ? r.content : t("no-comments"),
                      }}
                    />
                  </SectionCard>
                ))
              ) : (
                <EmptyState title={t("no-comments")} description="" />
              )}
            </div>
          )}

          {activeTab === "pdf" &&
            study.reportFile &&
            study.reportFile.length > 0 && (
              <ReportViewer study={study} activeTab="3" />
            )}
        </div>
      </PageContainer>

      <Modal
        isOpen={modalOpen}
        onClose={toggleModal}
        title={t("consultation") || "Fragen?"}
        size="md"
      >
        <p>{t("consultation-text-1")}</p>
        <p>{t("consultation-text-2")}</p>
        <p>{t("consultation-text-3")}</p>
        <div
          style={{
            display: "flex",
            gap: "var(--space-2)",
            justifyContent: "flex-end",
            marginTop: "var(--space-4)",
            flexWrap: "wrap",
          }}
        >
          <Button variant="secondary" onClick={toggleModal}>
            {t("consultation-cancel") || t("go-back")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              toggleModal();
              history.push(`/admin/frametest/${study.doctor_id}`);
            }}
          >
            {t("consultation-confirm") || t("consultation") || "Fragen?"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default LaboratoryDetail;
// #ux-redesign-end
